import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { forwardRef, useCallback, useMemo, type ChangeEvent } from 'react'

import { FormInput, type FormInputProps } from '@/components'

dayjs.extend(localizedFormat)

export interface DateInputProps extends Omit<FormInputProps, 'children'> {
  datepickerProps?: any // Not used in web version
  inputProps?: any // Not used in web version
  inputFieldProps?: any // Not used in web version
  onBlur?: () => void
  onChange: (value: Date) => void
  onClose?: () => void
  value: Date
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      accessibilityLabel: _accessibilityLabel,
      helper,
      isDisabled,
      isRequired,
      label,
      onBlur,
      onChange,
      onClose,
      value,
      ...props
    },
    ref
  ) => {
    const maximumDate = useMemo(() => dayjs().subtract(18, 'years').toDate(), [])
    const displayValue = useMemo(() => dayjs(value).format('YYYY-MM-DD'), [value])

    return (
      <FormInput
        helper={helper}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        {...props}
      >
        <input
          aria-disabled={isDisabled}
          aria-label={useMemo(
            () => _accessibilityLabel ?? `${label}${isRequired ? '. Required' : ''}${helper ? `. ${helper}` : ''}`,
            [isRequired, helper, label, _accessibilityLabel]
          )}
          aria-required={isRequired}
          className='h-10 w-full rounded border border-gray-300 bg-white pl-3 pr-2 font-poppinsRegular text-base text-black focus:outline-none'
          disabled={isDisabled}
          onBlur={useCallback(() => {
            onBlur?.()
            onClose?.()
          }, [onBlur, onClose])}
          onChange={useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
              if (e.target.value) {
                const newDate = dayjs(e.target.value).toDate()
                if (newDate.getTime() <= maximumDate.getTime()) {
                  onChange(newDate)
                }
              }
            },
            [maximumDate, onChange]
          )}
          max={useMemo(() => dayjs(maximumDate).format('YYYY-MM-DD'), [maximumDate])}
          ref={ref}
          type='date'
          value={displayValue}
        />
      </FormInput>
    )
  }
)

DateInput.displayName = 'DateInput'
